import {useEffect, useState} from "react";
import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import {hintColor} from "../../../../../AppStyles.ts";
import type {ContentPage} from "../../../../../sdo/ContentPage.ts";
import type {ContentFramePointer} from "../../../../../sdo/ContentFramePointer.ts";
import ContentFrameCard from "../../../intel/panels/resources/ContentFrameCard.tsx";
import ResizableSplit from "../../../../common/ResizableSplit.tsx";

const MAX_RENDER_HEIGHT = 800;

interface ContentPageDetailProps {
    page: ContentPage;
    frames: ContentFramePointer[];
    onClose: () => void;
}

function ContentPageDetail({page, frames, onClose}: ContentPageDetailProps) {
    const [zoom, setZoom] = useState(1.0);
    const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

    // Reset zoom when page changes
    useEffect(() => {
        setZoom(1.0);
        setNaturalSize(null);
    }, [page.pageNumber]);

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setNaturalSize({w: img.naturalWidth, h: img.naturalHeight});
    };

    const zoomIn = () => setZoom((z) => z * 1.25);
    const zoomOut = () => setZoom((z) => Math.max(z / 1.25, 0.1));
    const zoomReset = () => setZoom(1.0);

    // Compute display dimensions matching desktop applyZoom() logic
    let displayWidth: number | undefined;
    let displayHeight: number | undefined;
    if (naturalSize) {
        const {w, h} = naturalSize;
        if (h > MAX_RENDER_HEIGHT && zoom <= 1.0) {
            const baseScale = MAX_RENDER_HEIGHT / h;
            displayHeight = MAX_RENDER_HEIGHT * zoom;
            displayWidth = w * baseScale * zoom;
        } else {
            displayHeight = h * zoom;
            displayWidth = w * zoom;
        }
    }

    const imgSrc = page.renderUrl || page.thumbnailUrl || "/images/content_pages.svg";

    const zoomButtonSx = {
        border: "0.5px solid #3F464F",
        borderRadius: "6px",
        color: "#a8b8c0",
        padding: "4px",
        "&:hover": {borderColor: "#7093a0", color: "#f2f5fa"},
    };

    return (
        <Stack direction="column" spacing={1} sx={{height: "100%", minHeight: 0}}>
            {/* Header: page title + close button */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{color: "#f2f5fa", fontSize: "1.05rem", fontWeight: 700}}>
                    Page {page.pageNumber}
                </Typography>
                <Tooltip title="Close detail" placement="left">
                    <IconButton onClick={onClose} size="small" sx={{color: "#a8b8c0", "&:hover": {color: hintColor}}}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Resizable vertical split: page preview (top) + frames (bottom) */}
            <ResizableSplit
                direction="vertical"
                defaultRatio={0.6}
                minRatio={0.2}
                maxRatio={0.85}
                first={(
                    <Box sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #3F464F",
                        borderRadius: "8px",
                        overflow: "hidden",
                    }}>
                        {/* Zoom controls */}
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center"
                               sx={{padding: "6px 8px", borderBottom: "1px solid #3F464F", flexShrink: 0}}>
                            <Tooltip title="Zoom out">
                                <IconButton onClick={zoomOut} size="small" sx={zoomButtonSx}>
                                    <RemoveIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Typography sx={{
                                color: "#a8b8c0",
                                fontSize: "0.80rem",
                                fontFamily: "monospace",
                                minWidth: "42px",
                                textAlign: "center",
                            }}>
                                {Math.round(zoom * 100)}%
                            </Typography>
                            <Tooltip title="Zoom in">
                                <IconButton onClick={zoomIn} size="small" sx={zoomButtonSx}>
                                    <AddIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset zoom">
                                <IconButton onClick={zoomReset} size="small" sx={zoomButtonSx}>
                                    <RestartAltIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        {/* Page render image (scrollable when zoomed beyond available space) */}
                        <Box sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: "auto",
                            display: "flex",
                            justifyContent: "center",
                            padding: "8px",
                        }}>
                            <Box
                                component="img"
                                src={imgSrc}
                                alt={`Page ${page.pageNumber} render`}
                                onLoad={handleImageLoad}
                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                    const img = e.target as HTMLImageElement;
                                    if (page.thumbnailUrl && img.src !== page.thumbnailUrl) {
                                        img.src = page.thumbnailUrl;
                                    } else {
                                        img.src = "/images/content_pages.svg";
                                    }
                                }}
                                sx={{
                                    ...(displayWidth && displayHeight
                                        ? {width: displayWidth, height: displayHeight}
                                        : {maxHeight: "100%", objectFit: "contain"}),
                                    borderRadius: "4px",
                                }}
                            />
                        </Box>
                    </Box>
                )}
                second={(
                    <Box sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #3F464F",
                        borderRadius: "8px",
                        overflow: "hidden",
                    }}>
                        <Box sx={{
                            padding: "8px 12px",
                            borderBottom: "1px solid #3F464F",
                            flexShrink: 0,
                        }}>
                            <Typography sx={{
                                color: "#a8b8c0",
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                letterSpacing: 0.3,
                            }}>
                                Content Frames {frames.length > 0 ? `(${frames.length})` : ""}
                            </Typography>
                        </Box>
                        <Box sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: "auto",
                            padding: "10px",
                        }}>
                            {frames.length > 0 ? (
                                <Stack direction="column" spacing={1}>
                                    {frames.map((frame) => (
                                        <ContentFrameCard key={frame.uid} frame={frame}/>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography sx={{
                                    color: "rgba(242, 245, 250, 0.4)",
                                    fontSize: "0.80rem",
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    padding: "12px",
                                }}>
                                    No content frames on this page
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            />
        </Stack>
    );
}

export default ContentPageDetail;
